<template>
  <div class>
    <v-card elevation="0" outlined v-if="!loading && players" min-height="350" max-height="500" class="overflow-auto">
      <v-card-title class="py-1 white--text d-flex align-center" :class="$vuetify.theme.dark ? 'black' : 'primary'">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2" medium>emoji_flags</v-icon>
          <span class="white--text body-font body-1 font-weight-bold text-uppercase">{{$vuetify.breakpoint.mdAndUp ? $t("dashboards.nationalPlayers") : $t("dashboards.nationalPlayersShort") }}</span>
        </div>
      </v-card-title>
      <div class="pr-3 pb-2 pl-3 pt-3">
        <div v-for="(player) in players" :key="player.playerId" class="d-flex flex-column mb-2">
          <div class="d-flex align-center">
            <img
              :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/${player.playerId}.png`"
              @error="onImageLoadFailure($event, player)"
              class="mr-2"
              width="40"
              v-if="$vuetify.breakpoint.mdAndUp"
            />
            <div class="d-flex flex-column flex-grow-1">
              <div class="d-flex align-center">
                <router-link
                  :to="`/player/${player.playerId}`"
                  class="body-2 font-weight-bold text-ellipsis" :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'"
                >{{player.name}}</router-link>
                <router-link class="d-flex align-center body-2 ml-2" :to="`/team/${player.teamId}`">
                  <img
                    :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${player.teamId}.png`"
                    width="15"
                    class="mr-1"
                  />
                  <span>{{player.teamName}}</span>
                </router-link>
              </div>

              <div class="d-flex align-center">
                <div class="d-flex align-center"
                v-if="$vuetify.breakpoint.mdAndUp">
                  <v-chip
                    :color="getPositionChip(getFieldPositionIdByDefaultCode(player.position))"
                    text-color="white"
                    x-small
                    class="mr-1 monospace-font"
                  >{{  getPositionCode(getFieldPositionIdByDefaultCode(player.position)) }}</v-chip>
                  <v-chip
                    class="monospace-font"
                    v-if="player.secondPosition"
                    :color="getPositionChip(getFieldPositionIdByDefaultCode(player.secondPosition))"
                    text-color="white"
                    x-small
                  >{{  getPositionCode(getFieldPositionIdByDefaultCode(player.secondPosition)) }}</v-chip>
                </div>
                <v-divider vertical v-if="$vuetify.breakpoint.mdAndUp" class="mx-1"></v-divider>
                <div class="d-flex align-center mx-1" v-if="$vuetify.breakpoint.mdAndUp">
                  <span class="primary--text font-weight-bold body-2">{{currentUserCurrencySymbol}}</span>
                  <span
                    class="ml-1 primary--text font-weight-bold body-2"
                  >{{ (player.marketValue ? player.marketValue * 1000 : "-") | formatCash(currentUserRate)}}</span>
                </div>
                <v-divider vertical v-if="$vuetify.breakpoint.mdAndUp" class="mx-1"></v-divider>
                <div class="d-flex align-center ml-1" v-if="$vuetify.breakpoint.mdAndUp">
                  <span class="primary--text font-weight-bold body-2">{{player.age}}</span>
                  <span
                    class="ml-1 primary--text body-2"
                  >{{ $t("playerDetail.yearsOld") }}</span>
                </div>
              </div>
            </div>
          </div>
          <v-divider></v-divider>
          <div class="d-flex lightSecondary lighten-4 py-1 px-2 rounded-b">
            <div class="w-50 d-flex flex-column">
              <span class="subheader-font body-2">{{$t("charts.currentSeasonSelectText")}}</span>
              <span class="body-2">{{player.seasonTeamName}}</span>
              <div class="d-flex body-2">
                <div class="w-50">
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerDetail.playerUsage")}}</span>
                    <span>{{player.seasonMinsPlayed}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.goals")}}</span>
                    <span>{{player.seasonGoals}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.assists")}}</span>
                    <span>{{player.seasonAssists}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.expectedThreat")}}</span>
                    <span>{{player.seasonXT ? player.seasonXT.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.yellowCards")}}</span>
                    <span>{{player.seasonYellowCards}}</span>
                  </div>
                </div>
                <v-divider vertical v-if="$vuetify.breakpoint.mdAndUp" class="mx-1"></v-divider>
                <div class="w-50">
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("table.history.games")}}</span>
                    <span>{{player.gamesPlayed}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.xG")}}</span>
                    <span>{{player.seasonXG ? player.seasonXG.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.xA")}}</span>
                    <span>{{player.seasonXA ? player.seasonXA.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("statsNaming.xTOpenPlay")}}</span>
                    <span>{{player.seasonXTOpenPlay ? player.seasonXTOpenPlay.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.redCards")}}</span>
                    <span>{{player.seasonRedCards}}</span>
                  </div>
                </div>
              </div>
            </div>
            <v-divider vertical class="mx-1"></v-divider>
            <div class="w-50 d-flex flex-column">
              <span class="subheader-font body-2">{{$t("dashboards.lastGameSimple")}}</span>
              <div class="d-flex align-center body-2">
                <router-link
                  class="w-41 d-flex align-center"
                  :to="`/team/${player.homeTeamId}`"
                >
                  <div>
                    <v-img
                      :src="player.homeTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + player.homeTeamId + '.png'"
                      :height="18"
                      :width="18"
                      class="mx-1"
                    />
                  </div>
                  <span>{{player.homeTeamName}}</span>
                </router-link>
                <div
                  class="text-center subheader-font w-15"
                >
                  <span class="ml-1">{{player.homeTeamScore}}</span>
                  <span class="mx-1">-</span>
                  <span class="mr-1">{{player.awayTeamScore}}</span>
                </div>
                <router-link class="w-41 d-flex align-center justify-end" :to="`/team/${player.awayTeamId}`">
                  <span>{{player.awayTeamName}}</span>
                  <div>
                    <v-img
                      :src="player.awayTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + player.awayTeamId + '.png'"
                      :height="18"
                      :width="18"
                      class="mx-1"
                    />
                  </div>
                </router-link>
                <div class="w-10">
                  <v-btn block color="primary" :to="`/game/${player.gameId}`" x-small class="elevation-0">
                    <v-icon small>chevron_right</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class="d-flex body-2">
                <div class="w-50">
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerDetail.playerUsage")}}</span>
                    <span>{{player.minsPlayed}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.goals")}}</span>
                    <span>{{player.goals}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.assists")}}</span>
                    <span>{{player.assists}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.expectedThreat")}}</span>
                    <span>{{player.xT ? player.xT.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.yellowCards")}}</span>
                    <span>{{player.minYellowCard ? 1 : (player.minSecondYellowCard ? 2 : 0)}}</span>
                  </div>
                </div>
                <v-divider vertical v-if="$vuetify.breakpoint.mdAndUp" class="mx-1"></v-divider>
                <div class="w-50">
                  <div class="d-flex align-center justify-space-between">
                    <span class="primary--text text--lighten-2">-</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.xG")}}</span>
                    <span>{{player.xG ? player.xG.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.xA")}}</span>
                    <span>{{player.xA ? player.xA.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("statsNaming.xTOpenPlay")}}</span>
                    <span>{{player.xTOpenPlay ? player.xTOpenPlay.toFixed(2) : 0}}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="subheader-font">{{$t("playerScout.redCards")}}</span>
                    <span>{{player.minRedCard ? 1 : 0}}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
    <v-card class="d-flex justify-center align-center py-8 px-12" v-if="loading">
      <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
    </v-card>
  </div>
</template>

<script>
import GameService from "../../../services/game/game.service";
import { formatCash, getChipColorByPosition, getCurrencies, getPositionCode, getFieldPositionIdByDefaultCode } from "../../../utils/functions";
import DriblabIcon from '../../base/DriblabIcon';
import  DriblabCountryFlag from '../../base/DriblabCountryFlag';
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: "NationalPlayersWidget",
  components: {
    DriblabIcon,
    DriblabCountryFlag
  },
  props: {
    countryId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      players: null,
      loading: false,
      observer: null
    };
  },
  filters: {
    formatCash(n,rate) {
      return formatCash(n * rate)
    }
  },
  computed:{
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserCurrency(){
      return this.$store.getters["auth/currency"];
    },
    currentUserRate(){
      let rate = this.currentUserCurrency === "EUR" ? 1 : this.$store.getters["exchange/rates"][this.$store.getters["auth/currency"]];
      return rate ? rate : 1;
    },
    currencies(){
      return getCurrencies();
    },
    currentUserCurrencySymbol(){
      let currentCurrencyObj = _.find(this.currencies,(c) => c.code === this.currentUserCurrency)
      return currentCurrencyObj.symbol;
    }
  },
  mounted() {
    this.loadViewData();
    this.observer = new ResizeObserver(this.onResize).observe(this.$el);
  },
  methods: {
    async loadViewData() {
      await this.loadGames();
    },
    async loadGames() {
      this.loading = true;
      let gameResponse = await GameService.getLatestGamesNationalPLayers(
        this.countryId,50
      );
      this.players = _.chain(gameResponse.data).map(g =>{ g.marketValue = g.marketValue || 0;return g}).orderBy("marketValue","desc").slice(0,20).value();
      this.loading = false;
    },
    onImgError(event, item, side) {
      if (side === "home") {
        item.homeTeamImgError = true;
      } else if (side === "away") {
        item.awayTeamImgError = true;
      }
    },
    onResize() {
      this.$emit("heightChange", this.$el.clientHeight);
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    onImageLoadFailure(event, player){
      event.target.src = `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/faces/0_${player && player.male === false ? 'f' : 'm'}.png`;
    },
    getFieldPositionIdByDefaultCode(position) {
      return getFieldPositionIdByDefaultCode(position);
    },
  },
  destroyed() {
    delete this.observer;
  }
};
</script>
